// ═══════════════════════════════════════════════════════════════════════
// Temps › Réglages › Horaires — modèles d'horaire datés, affectations, horaires d'un jour.
// • Un modèle décrit chaque jour (travaillé ou repos, début, fin, pause) ; un changement d'horaire
//   est une NOUVELLE VERSION datée (les journées passées gardent l'ancienne).
// • Une version ne s'applique qu'après validation par une autre personne que son auteur.
// • L'horaire d'un salarié vient, dans l'ordre : de son horaire du jour, de son affectation
//   personnelle, puis de celle de son poste, service, département ou site. Sans horaire, aucune
//   durée n'est supposée.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Clock, Plus, CheckCircle2, Archive, CopyPlus, UserCheck, CalendarClock, Search, Ban, CalendarX } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, BandeauInfo, EtatVide, Squelette, Onglets, formaterDate } from '../../design/composants';
import {
  lire, ecrire, messageErreur, useReglages, useChargement, NavReglages, Fenetre, Champ, PiedFenetre, BadgeStatut,
  heureDakar, duree, nomPersonne, JOURS_SEMAINE,
} from './reglagesCommun';

const aujourdHui = () => new Date().toISOString().slice(0, 10);
const GROUPES = { SITE: 'Site', SERVICE: 'Service', DEPARTEMENT: 'Département', POSTE: 'Poste' };
const OPTIONS_GROUPE = { SITE: 'sites', SERVICE: 'services', DEPARTEMENT: 'departements', POSTE: 'postes' };
const PAUSES = { DEDUITE: 'Déduite automatiquement', POINTEE: 'Pointée par le salarié', AUCUNE: 'Sans pause' };
const nomJour = (type, rang) => (type === 'HEBDO' ? JOURS_SEMAINE[rang - 1] : `Jour ${rang}`);
const jourVide = (rang) => ({ rang, travaille: false, debut: '', fin: '', fin_lendemain: false, pause_min: '', pause_debut: '' });

/** Minutes théoriques d'un jour du formulaire (fin − début − pause), ou null si incomplet. */
function dureeFormulaire(j) {
  if (!j.travaille || !/^\d{2}:\d{2}$/.test(j.debut) || !/^\d{2}:\d{2}$/.test(j.fin)) return null;
  const m = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const d = m(j.debut); const f = m(j.fin) + (j.fin_lendemain ? 1440 : 0);
  return f > d ? f - d - (Number(j.pause_min) || 0) : null;
}

/** Résumé lisible des jours d'une version : « Lundi, Mardi : 08:00–17:00, pause 60 min (8 h 00) ». */
function resumeJours(version) {
  const groupes = [];
  for (const j of version.jours) {
    const texte = j.travaille
      ? `${j.debut}–${j.fin}${j.finLendemain ? ' (lendemain)' : ''}${j.pauseMin ? `, pause ${j.pauseMin} min${j.pauseDebut ? ` à ${j.pauseDebut}` : ''}` : ''} (${duree(j.dureeTheoriqueMin)})`
      : 'repos';
    const dernier = groupes[groupes.length - 1];
    if (dernier && dernier.texte === texte) dernier.jours.push(j.rang); else groupes.push({ texte, jours: [j.rang] });
  }
  return groupes.map((g) => `${g.jours.map((r) => nomJour(version.type_modele, r)).join(', ')} : ${g.texte}`);
}

// ─── Fenêtre : nouveau modèle ou nouvelle version ────────────────────────────────────────────
function FenetreModele({ base, sites, onFermer, onTermine }) {
  const depuisBase = base ? base.jours.map((j) => ({
    rang: j.rang, travaille: j.travaille, debut: j.debut || '', fin: j.fin || '', fin_lendemain: j.finLendemain, pause_min: j.pauseMin || '', pause_debut: j.pauseDebut || '',
  })) : [1, 2, 3, 4, 5, 6, 7].map(jourVide);
  const [f, setF] = useState({
    code: base?.code || '', nom: base?.nom || '', type_modele: base?.type_modele || 'HEBDO', longueur_cycle: base?.longueur_cycle || 7,
    date_debut: aujourdHui(), pause_mode: base?.pause_mode || 'DEDUITE', travaille_feries: base?.travaille_feries === true, marge_avant_min: base?.marge_avant_min ?? '', marge_apres_min: base?.marge_apres_min ?? '',
    site_id: base?.site_id || '', jours: depuisBase,
  });
  const [erreur, setErreur] = useState('');
  const changerType = (type, longueur) => {
    const n = type === 'HEBDO' ? 7 : Math.max(1, Math.min(366, Number(longueur) || 1));
    setF({ ...f, type_modele: type, longueur_cycle: n, jours: Array.from({ length: n }, (_, i) => f.jours[i] || jourVide(i + 1)) });
  };
  const majJour = (i, k, v) => setF({ ...f, jours: f.jours.map((j, x) => (x === i ? { ...j, [k]: v } : j)) });
  const recopier = () => {
    const modele = f.jours.find((j) => j.travaille);
    if (modele) setF({ ...f, jours: f.jours.map((j) => (j.travaille ? { ...modele, rang: j.rang } : j)) });
  };
  const envoyer = async () => {
    const corps = {
      ...f, site_id: f.site_id || null,
      travaille_feries: f.travaille_feries === true,
      marge_avant_min: f.marge_avant_min === '' ? null : Number(f.marge_avant_min), marge_apres_min: f.marge_apres_min === '' ? null : Number(f.marge_apres_min),
      longueur_cycle: f.type_modele === 'CYCLE' ? Number(f.longueur_cycle) : null,
      jours: f.jours.map((j) => (j.travaille
        ? { rang: j.rang, travaille: true, debut: j.debut, fin: j.fin, fin_lendemain: j.fin_lendemain, pause_min: f.pause_mode === 'AUCUNE' ? 0 : Number(j.pause_min) || 0, pause_debut: f.pause_mode === 'AUCUNE' ? null : (j.pause_debut || null) }
        : { rang: j.rang, travaille: false })),
    };
    try {
      if (base) await ecrire('post', `/modeles/${encodeURIComponent(base.code)}/versions`, corps);
      else await ecrire('post', '/modeles', corps);
      onTermine(base ? 'Nouvelle version enregistrée : elle s\'appliquera après validation.' : 'Modèle enregistré : il pourra être affecté après validation.');
    } catch (e) { setErreur(messageErreur(e)); }
  };
  return (
    <Fenetre titre={base ? `Nouvelle version de « ${base.nom} »` : 'Nouveau modèle d\'horaire'} Icone={base ? CopyPlus : Plus} onFermer={onFermer} large>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {base && <BandeauInfo ton="info">Les journées avant la date d'effet gardent la version actuelle. La version actuelle se termine la veille.</BandeauInfo>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 16px' }}>
        {!base && <Champ libelle="Code" requis aide="Ex. ADMIN, ABATTOIR-MATIN"><input className="rh-champ-saisie" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} /></Champ>}
        <Champ libelle="Nom" requis><input className="rh-champ-saisie" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} placeholder="Ex. Administratif du siège" /></Champ>
        <Champ libelle="Date d'effet" requis><input type="date" className="rh-champ-saisie" value={f.date_debut} onChange={(e) => setF({ ...f, date_debut: e.target.value })} /></Champ>
        <Champ libelle="Rythme">
          <select className="rh-champ-saisie" value={f.type_modele} onChange={(e) => changerType(e.target.value, f.longueur_cycle)}>
            <option value="HEBDO">Semaine (lundi à dimanche)</option><option value="CYCLE">Cycle de plusieurs jours (roulement)</option>
          </select>
        </Champ>
        {f.type_modele === 'CYCLE' && <Champ libelle="Nombre de jours du cycle" requis><input type="number" min="1" max="366" className="rh-champ-saisie" value={f.longueur_cycle} onChange={(e) => changerType('CYCLE', e.target.value)} /></Champ>}
        <Champ libelle="Pause"><select className="rh-champ-saisie" value={f.pause_mode} onChange={(e) => setF({ ...f, pause_mode: e.target.value })}>{Object.entries(PAUSES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Champ>
        <Champ libelle="Site (facultatif)">
          <select className="rh-champ-saisie" value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })}>
            <option value="">Tous les sites</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.libelle}</option>)}
          </select>
        </Champ>
        <Champ libelle="Jours fériés" aide="Par défaut, un jour férié est chômé : aucun travail n'est attendu ce jour-là. Cochez si cette population travaille normalement les fériés (abattoir, gardiennage…)."><label><input type="checkbox" checked={f.travaille_feries === true} onChange={(e) => setF({ ...f, travaille_feries: e.target.checked })} /> Travaille les jours fériés</label></Champ>
        <Champ libelle="Marge avant le début (min)" aide="Un passage à la borne dans cette marge appartient à la journée."><input type="number" min="0" max="720" className="rh-champ-saisie" value={f.marge_avant_min} onChange={(e) => setF({ ...f, marge_avant_min: e.target.value })} /></Champ>
        <Champ libelle="Marge après la fin (min)"><input type="number" min="0" max="720" className="rh-champ-saisie" value={f.marge_apres_min} onChange={(e) => setF({ ...f, marge_apres_min: e.target.value })} /></Champ>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0' }}>
        <strong style={{ fontSize: 14 }}>Jours</strong>
        <Bouton petit variante="discret" Icone={CopyPlus} onClick={recopier}>Recopier le premier jour travaillé sur les autres</Bouton>
      </div>
      <div className="rh-conteneur-tableau">
        <table className="rh-tableau">
          <thead><tr><th>Jour</th><th>Travaillé</th><th>Début</th><th>Fin</th><th>Fin le lendemain</th>{f.pause_mode !== 'AUCUNE' && <><th>Pause (min)</th><th>Pause à</th></>}<th>Durée prévue</th></tr></thead>
          <tbody>
            {f.jours.map((j, i) => (
              <tr key={j.rang}>
                <td>{nomJour(f.type_modele, j.rang)}</td>
                <td><input type="checkbox" checked={j.travaille} onChange={(e) => majJour(i, 'travaille', e.target.checked)} aria-label={`${nomJour(f.type_modele, j.rang)} travaillé`} /></td>
                <td><input type="time" className="rh-champ-saisie" disabled={!j.travaille} value={j.debut} onChange={(e) => majJour(i, 'debut', e.target.value)} aria-label="Début" /></td>
                <td><input type="time" className="rh-champ-saisie" disabled={!j.travaille} value={j.fin} onChange={(e) => majJour(i, 'fin', e.target.value)} aria-label="Fin" /></td>
                <td><input type="checkbox" disabled={!j.travaille} checked={j.fin_lendemain} onChange={(e) => majJour(i, 'fin_lendemain', e.target.checked)} aria-label="Fin le lendemain" /></td>
                {f.pause_mode !== 'AUCUNE' && (
                  <>
                    <td><input type="number" min="0" max="480" className="rh-champ-saisie" style={{ width: 80 }} disabled={!j.travaille} value={j.pause_min} onChange={(e) => majJour(i, 'pause_min', e.target.value)} aria-label="Pause en minutes" /></td>
                    <td><input type="time" className="rh-champ-saisie" disabled={!j.travaille} value={j.pause_debut} onChange={(e) => majJour(i, 'pause_debut', e.target.value)} aria-label="Début de pause" /></td>
                  </>
                )}
                <td>{j.travaille ? (dureeFormulaire(j) != null ? duree(dureeFormulaire(j)) : '—') : 'Repos'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="rh-form-aide">L'heure de pause sert à découper la journée en matin et après-midi (permissions d'une demi-journée).</p>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={Plus} onClick={envoyer} disabled={!f.nom.trim() || (!base && !f.code.trim()) || !f.date_debut}>Enregistrer</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

// ─── Fenêtre : affectation ───────────────────────────────────────────────────────────────────
function FenetreAffectation({ modeles, options, onFermer, onTermine }) {
  const valides = modeles.map((m) => ({ ...m, valide: m.versions.find((v) => v.statut_modele === 'VALIDE') })).filter((m) => m.valide);
  const [f, setF] = useState({ cible: 'SALARIE', employe_id: '', groupe_id: '', shift_id: '', date_debut: aujourdHui(), date_fin: '', rang_depart: 1, motif: '' });
  const [erreur, setErreur] = useState('');
  const modele = valides.find((m) => m.valide.id === f.shift_id)?.valide;
  const envoyer = async () => {
    try {
      const a = await ecrire('post', '/affectations', {
        employe_id: f.cible === 'SALARIE' ? f.employe_id : null, groupe_type: f.cible === 'SALARIE' ? null : f.cible, groupe_id: f.cible === 'SALARIE' ? null : f.groupe_id,
        shift_id: f.shift_id, date_debut: f.date_debut, date_fin: f.date_fin || null, rang_depart: Number(f.rang_depart) || 1, motif: f.motif || null,
      });
      onTermine(a?.statut === 'A_VALIDER'
        ? 'Affectation enregistrée « à valider » : elle commence avant aujourd\'hui et modifie des journées passées. Une autre personne habilitée doit la valider avant qu\'elle s\'applique.'
        : 'Horaire affecté.');
    } catch (e) { setErreur(messageErreur(e)); }
  };
  return (
    <Fenetre titre="Affecter un horaire" Icone={UserCheck} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {valides.length === 0 && <BandeauInfo ton="alerte">Aucun modèle validé : faites d'abord valider un modèle.</BandeauInfo>}
      <Champ libelle="Pour" aide="L'affectation personnelle l'emporte sur celle du poste, puis du service, du département et du site.">
        <select className="rh-champ-saisie" value={f.cible} onChange={(e) => setF({ ...f, cible: e.target.value, groupe_id: '' })}>
          <option value="SALARIE">Un salarié</option>{Object.entries(GROUPES).map(([v, l]) => <option key={v} value={v}>Un {l.toLowerCase()} entier</option>)}
        </select>
      </Champ>
      {f.cible === 'SALARIE' ? (
        <Champ libelle="Salarié" requis>
          <select className="rh-champ-saisie" value={f.employe_id} onChange={(e) => setF({ ...f, employe_id: e.target.value })}>
            <option value="">Choisir…</option>{options.employes.map((e) => <option key={e.id} value={e.id}>{nomPersonne(e.prenom, e.nom)} ({e.matricule})</option>)}
          </select>
        </Champ>
      ) : (
        <Champ libelle={GROUPES[f.cible]} requis>
          <select className="rh-champ-saisie" value={f.groupe_id} onChange={(e) => setF({ ...f, groupe_id: e.target.value })}>
            <option value="">Choisir…</option>{(options[OPTIONS_GROUPE[f.cible]] || []).map((g) => <option key={g.id} value={g.id}>{g.libelle}</option>)}
          </select>
        </Champ>
      )}
      <Champ libelle="Modèle d'horaire" requis>
        <select className="rh-champ-saisie" value={f.shift_id} onChange={(e) => setF({ ...f, shift_id: e.target.value, rang_depart: 1 })}>
          <option value="">Choisir…</option>{valides.map((m) => <option key={m.code} value={m.valide.id}>{m.nom} ({m.code})</option>)}
        </select>
      </Champ>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
        <Champ libelle="Du" requis><input type="date" className="rh-champ-saisie" value={f.date_debut} onChange={(e) => setF({ ...f, date_debut: e.target.value })} /></Champ>
        <Champ libelle="Au (facultatif)"><input type="date" className="rh-champ-saisie" value={f.date_fin} onChange={(e) => setF({ ...f, date_fin: e.target.value })} /></Champ>
      </div>
      {modele?.type_modele === 'CYCLE' && (
        <Champ libelle="Jour du cycle à la date de début" aide={`De 1 à ${modele.longueur_cycle}.`}>
          <input type="number" min="1" max={modele.longueur_cycle} className="rh-champ-saisie" value={f.rang_depart} onChange={(e) => setF({ ...f, rang_depart: e.target.value })} />
        </Champ>
      )}
      <Champ libelle="Motif (facultatif)"><input className="rh-champ-saisie" value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={UserCheck} onClick={envoyer} disabled={!f.shift_id || !f.date_debut || (f.cible === 'SALARIE' ? !f.employe_id : !f.groupe_id)}>Affecter</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

// ─── Fenêtre : horaire d'un jour ─────────────────────────────────────────────────────────────
function FenetreHoraireJour({ options, moi, onFermer, onTermine }) {
  const [f, setF] = useState({ employe_id: '', date_jour: aujourdHui(), travaille: true, debut: '', fin: '', fin_lendemain: false, pause_min: '', pause_debut: '', motif: '' });
  const [erreur, setErreur] = useState('');
  const envoyer = async () => {
    try {
      await ecrire('post', '/horaires-particuliers', { ...f, pause_min: Number(f.pause_min) || 0, pause_debut: f.pause_debut || null });
      onTermine('Horaire du jour enregistré.');
    } catch (e) { setErreur(messageErreur(e)); }
  };
  return (
    <Fenetre titre="Horaire d'un jour" Icone={CalendarClock} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--sg-texte-2)' }}>Échange, astreinte ou horaire exceptionnel : remplace l'horaire habituel ce jour-là. Vous ne pouvez pas le saisir pour vous-même.</p>
      <Champ libelle="Salarié" requis>
        <select className="rh-champ-saisie" value={f.employe_id} onChange={(e) => setF({ ...f, employe_id: e.target.value })}>
          <option value="">Choisir…</option>{options.employes.filter((e) => e.id !== moi).map((e) => <option key={e.id} value={e.id}>{nomPersonne(e.prenom, e.nom)} ({e.matricule})</option>)}
        </select>
      </Champ>
      <Champ libelle="Date" requis><input type="date" className="rh-champ-saisie" value={f.date_jour} onChange={(e) => setF({ ...f, date_jour: e.target.value })} /></Champ>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 12 }}>
        <input type="checkbox" checked={f.travaille} onChange={(e) => setF({ ...f, travaille: e.target.checked })} /> Jour travaillé (décocher pour un jour de repos)
      </label>
      {f.travaille && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0 12px' }}>
          <Champ libelle="Début" requis><input type="time" className="rh-champ-saisie" value={f.debut} onChange={(e) => setF({ ...f, debut: e.target.value })} /></Champ>
          <Champ libelle="Fin" requis><input type="time" className="rh-champ-saisie" value={f.fin} onChange={(e) => setF({ ...f, fin: e.target.value })} /></Champ>
          <Champ libelle="Pause (min)"><input type="number" min="0" max="480" className="rh-champ-saisie" value={f.pause_min} onChange={(e) => setF({ ...f, pause_min: e.target.value })} /></Champ>
          <Champ libelle="Pause à"><input type="time" className="rh-champ-saisie" value={f.pause_debut} onChange={(e) => setF({ ...f, pause_debut: e.target.value })} /></Champ>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 12 }}>
            <input type="checkbox" checked={f.fin_lendemain} onChange={(e) => setF({ ...f, fin_lendemain: e.target.checked })} /> Fin le lendemain
          </label>
        </div>
      )}
      <Champ libelle="Motif" requis><input className="rh-champ-saisie" value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} placeholder="Ex. échange avec un collègue, astreinte" /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={CalendarClock} onClick={envoyer} disabled={!f.employe_id || !f.date_jour || f.motif.trim().length < 3 || (f.travaille && (!f.debut || !f.fin))}>Enregistrer</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

function FenetreMotif({ titre, Icone, avecDate, onFermer, onConfirmer }) {
  const [motif, setMotif] = useState('');
  const [date, setDate] = useState(aujourdHui());
  const [erreur, setErreur] = useState('');
  return (
    <Fenetre titre={titre} Icone={Icone} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {avecDate && <Champ libelle="Dernier jour" requis><input type="date" className="rh-champ-saisie" value={date} onChange={(e) => setDate(e.target.value)} /></Champ>}
      <Champ libelle="Motif" requis><textarea className="rh-champ-saisie" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante="danger" Icone={Icone} disabled={motif.trim().length < 3}
          onClick={async () => { try { await onConfirmer(motif, date); } catch (e) { setErreur(messageErreur(e)); } }}>{titre}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

// ─── Onglets ─────────────────────────────────────────────────────────────────────────────────
function OngletModeles({ droits, modeles, onAction, ouvrir }) {
  if (!modeles) return <Squelette largeur="60%" />;
  if (modeles.length === 0) return <EtatVide Icone={Clock} titre="Aucun modèle d'horaire" texte="Sans horaire, une journée est « sans horaire » : ni retard, ni absence, ni durée prévue." />;
  return modeles.map((m) => (
    <Carte key={m.code} titre={`${m.nom} · ${m.code}`} Icone={Clock}
      action={droits?.proposer && <Bouton petit Icone={CopyPlus} onClick={() => ouvrir({ type: 'modele', base: m.versions[0] })}>Nouvelle version</Bouton>}>
      <ul className="rh-liste">
        {m.versions.map((v) => (
          <li key={v.id} style={{ flexWrap: 'wrap', alignItems: 'flex-start', opacity: v.statut_modele === 'ARCHIVE' ? 0.6 : 1 }}>
            <span style={{ flex: 1, minWidth: 260 }}>
              <strong>Version {v.version}</strong> · à partir du {formaterDate(v.date_debut, true)}{v.date_fin ? ` jusqu'au ${formaterDate(v.date_fin, true)}` : ''}
              <span className="rh-cellule-secondaire" style={{ display: 'block' }}>
                {v.type_modele === 'CYCLE' ? `Cycle de ${v.longueur_cycle} jours` : 'Semaine'} · pause {PAUSES[v.pause_mode].toLowerCase()} · {v.travaille_feries ? 'travaille les fériés' : 'fériés chômés'}
                {v.site_nom ? ` · site ${v.site_nom}` : ''} · proposée par {nomPersonne(v.auteur_prenom, v.auteur_nom)}
                {v.valideur_nom ? `, validée par ${nomPersonne(v.valideur_prenom, v.valideur_nom)}` : ''}
              </span>
              {resumeJours(v).map((l) => <span key={l} style={{ display: 'block', fontSize: 13 }}>{l}</span>)}
            </span>
            <BadgeStatut statut={v.statut_modele === 'BROUILLON' ? 'A_VALIDER' : v.statut_modele} />
            {v.statut_modele === 'BROUILLON' && droits?.valider && (
              <Bouton petit Icone={CheckCircle2} disabled={v.auteur_id === droits.moi}
                title={v.auteur_id === droits.moi ? 'Vous avez proposé cette version : une autre personne doit la valider.' : undefined}
                onClick={() => onAction(() => ecrire('patch', `/modeles/${v.id}/valider`), 'Version validée : les journées concernées seront recalculées.')}>Valider</Bouton>
            )}
            {v.statut_modele !== 'ARCHIVE' && (v.statut_modele === 'VALIDE' ? droits?.valider : droits?.proposer) && (
              <Bouton petit variante="discret" Icone={Archive} onClick={() => ouvrir({ type: 'archiver', version: v })}>Archiver</Bouton>
            )}
          </li>
        ))}
      </ul>
    </Carte>
  ));
}

function OngletAffectations({ droits, affectations, ouvrir, onAction }) {
  if (!affectations) return <Squelette largeur="60%" />;
  if (affectations.length === 0) return <EtatVide Icone={UserCheck} titre="Aucune affectation" texte="Affectez un modèle validé à un salarié, ou à un poste, un service, un département ou un site." />;
  return (
    <div className="rh-conteneur-tableau">
      <table className="rh-tableau">
        <thead><tr><th>Pour</th><th>Horaire</th><th>Période</th><th>Statut</th><th aria-label="Actions" /></tr></thead>
        <tbody>
          {affectations.map((a) => (
            <tr key={a.id}>
              <td>{a.employe_id ? <>{nomPersonne(a.employe_prenom, a.employe_nom)}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{a.matricule}</span></>
                : <>{GROUPES[a.groupe_type]} {a.groupe_libelle}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>Tout le groupe</span></>}</td>
              <td>{a.modele_nom}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{a.modele_code}{a.type_modele === 'CYCLE' ? ` · départ au jour ${a.rang_depart}` : ''}</span></td>
              <td>Du {formaterDate(a.date_debut)}{a.date_fin ? ` au ${formaterDate(a.date_fin)}` : ' (sans fin)'}</td>
              <td><BadgeStatut statut={a.statut} /></td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {a.statut === 'A_VALIDER' && droits?.valider && a.employe_id !== droits.moi && (
                  <Bouton petit Icone={CheckCircle2} disabled={a.auteur_id === droits.moi}
                    title={a.auteur_id === droits.moi ? 'Vous avez proposé cette affectation : une autre personne doit la valider.' : undefined}
                    onClick={() => onAction(() => ecrire('patch', `/affectations/${a.id}/valider`), 'Affectation validée : les journées concernées seront recalculées.')}>Valider</Bouton>
                )}
                {['ACTIVE', 'A_VALIDER'].includes(a.statut) && droits?.proposer && a.employe_id !== droits.moi && (
                  <>
                    {a.statut === 'ACTIVE' && <Bouton petit variante="discret" Icone={CalendarX} onClick={() => ouvrir({ type: 'terminer', affectation: a })}>Terminer</Bouton>}
                    <Bouton petit variante="discret" Icone={Ban} onClick={() => ouvrir({ type: 'annulerAffectation', affectation: a })}>Annuler</Bouton>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OngletParticuliers({ droits, particuliers, ouvrir }) {
  if (!particuliers) return <Squelette largeur="60%" />;
  if (particuliers.length === 0) return <EtatVide Icone={CalendarClock} titre="Aucun horaire d'un jour" texte="Un échange ou une astreinte se saisit ici, pour un salarié et une date." />;
  return (
    <ul className="rh-liste">
      {particuliers.map((h) => (
        <li key={h.id} style={{ flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 240 }}>
            <strong>{nomPersonne(h.employe_prenom, h.employe_nom)}</strong> · {formaterDate(h.date_jour, true)}
            <span className="rh-cellule-secondaire" style={{ display: 'block' }}>
              {h.travaille ? `${h.debut}–${h.fin}${h.fin_lendemain ? ' (lendemain)' : ''}${h.pause_minutes ? `, pause ${h.pause_minutes} min` : ''}` : 'Repos'} · {h.motif}
            </span>
          </span>
          <BadgeStatut statut={h.statut} />
          {h.statut === 'ACTIVE' && droits?.proposer && h.employe_id !== droits.moi && (
            <Bouton petit variante="discret" Icone={Ban} onClick={() => ouvrir({ type: 'annulerParticulier', horaire: h })}>Annuler</Bouton>
          )}
        </li>
      ))}
    </ul>
  );
}

function OngletVerifier({ options }) {
  const [employeId, setEmployeId] = useState('');
  const [date, setDate] = useState(aujourdHui());
  const [resultat, setResultat] = useState(null);
  const [erreur, setErreur] = useState('');
  const verifier = async () => {
    try { setErreur(''); setResultat(await lire('/horaire-prevu', { employe_id: employeId, date })); } catch (e) { setErreur(messageErreur(e)); setResultat(null); }
  };
  const SOURCES = { PARTICULIER: 'horaire du jour', INDIVIDUEL: 'affectation personnelle', GROUPE: 'affectation de groupe' };
  const h = resultat?.horaire;
  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Champ libelle="Salarié">
          <select className="rh-champ-saisie" value={employeId} onChange={(e) => setEmployeId(e.target.value)}>
            <option value="">Choisir…</option>{options.employes.map((e) => <option key={e.id} value={e.id}>{nomPersonne(e.prenom, e.nom)} ({e.matricule})</option>)}
          </select>
        </Champ>
        <Champ libelle="Date"><input type="date" className="rh-champ-saisie" value={date} onChange={(e) => setDate(e.target.value)} /></Champ>
        <div style={{ marginBottom: 12 }}><Bouton variante="primaire" Icone={Search} onClick={verifier} disabled={!employeId || !date}>Vérifier</Bouton></div>
      </div>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {resultat && (resultat.sans_horaire ? (
        <BandeauInfo ton="alerte" titre="Sans horaire">Aucun horaire n'est affecté ce jour-là : aucune durée prévue, ni retard ni absence ne peuvent être constatés.</BandeauInfo>
      ) : !h.travaille ? (
        <BandeauInfo ton="info" titre="Jour de repos">Selon {SOURCES[h.source]}{h.code ? ` (modèle ${h.nom}, version ${h.version})` : ''}.</BandeauInfo>
      ) : (
        <BandeauInfo ton="succes" titre={`${heureDakar(h.debut)} – ${heureDakar(h.fin)} · durée prévue ${duree(h.dureeTheoriqueMin)}`}>
          Pause : {h.pauseMin ? `${h.pauseMin} min${h.pauseDebut ? ` à ${heureDakar(h.pauseDebut)}` : ''}` : 'aucune'}.
          {h.matin && ` Matin ${heureDakar(h.matin.debut)}–${heureDakar(h.matin.fin)}, après-midi ${heureDakar(h.apresMidi.debut)}–${heureDakar(h.apresMidi.fin)}.`}
          {' '}Selon {SOURCES[h.source]}{h.groupeType ? ` (${GROUPES[h.groupeType].toLowerCase()})` : ''}{h.code ? `, modèle ${h.nom}, version ${h.version}` : ''}.
        </BandeauInfo>
      ))}
    </>
  );
}

export default function ReglagesHorairesPage() {
  const { droits, options, erreur: erreurDroits } = useReglages();
  const [onglet, setOnglet] = useState('modeles');
  const [message, setMessage] = useState('');
  const [erreurAction, setErreurAction] = useState('');
  const [fenetre, setFenetre] = useState(null);
  const globale = !!droits?.lecture_globale;
  const modeles = useChargement(() => lire('/modeles', { archives: '1' }), []);
  const affectations = useChargement(() => (globale ? lire('/affectations') : Promise.resolve([])), [globale]);
  const particuliers = useChargement(() => (globale ? lire('/horaires-particuliers') : Promise.resolve([])), [globale]);

  const toutRecharger = () => { modeles.recharger(); affectations.recharger(); particuliers.recharger(); };
  const terminer = (m) => { setFenetre(null); setMessage(m); setErreurAction(''); toutRecharger(); };
  const agir = async (fn, m) => { try { await fn(); terminer(m); } catch (e) { setErreurAction(messageErreur(e)); } };
  const aValider = (modeles.donnees || []).flatMap((m) => m.versions).filter((v) => v.statut_modele === 'BROUILLON').length;
  const affectationsAValider = (affectations.donnees || []).filter((a) => a.statut === 'A_VALIDER').length;

  const actionPrincipale = droits?.proposer && {
    modeles: <Bouton variante="primaire" Icone={Plus} onClick={() => setFenetre({ type: 'modele' })}>Nouveau modèle</Bouton>,
    affectations: <Bouton variante="primaire" Icone={UserCheck} onClick={() => setFenetre({ type: 'affecter' })}>Affecter un horaire</Bouton>,
    particuliers: <Bouton variante="primaire" Icone={CalendarClock} onClick={() => setFenetre({ type: 'particulier' })}>Horaire d'un jour</Bouton>,
  }[onglet];

  const onglets = [
    { cle: 'modeles', libelle: 'Modèles', Icone: Clock, compte: aValider || undefined },
    ...(globale ? [
      { cle: 'affectations', libelle: 'Affectations', Icone: UserCheck },
      { cle: 'particuliers', libelle: 'Horaires d\'un jour', Icone: CalendarClock },
    ] : []),
    ...(options.employes.length ? [{ cle: 'verifier', libelle: 'Vérifier un horaire', Icone: Search }] : []),
  ];

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Temps', vers: '/temps' }, { libelle: 'Réglages' }, { libelle: 'Horaires' }]} titre="Horaires de travail"
          sousTitre="Chaque salarié reçoit l'horaire de son affectation personnelle, sinon celui de son poste, service, département ou site. Un changement d'horaire est une nouvelle version datée."
          actions={actionPrincipale} />
        <NavReglages actif="horaires" />
        {(erreurDroits || modeles.erreur || erreurAction) && <BandeauInfo ton="danger">{erreurDroits || modeles.erreur || erreurAction}</BandeauInfo>}
        {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
        {aValider > 0 && <BandeauInfo ton="alerte" titre={`${aValider} version${aValider > 1 ? 's' : ''} à valider`}>Une version non validée ne s'applique à personne.</BandeauInfo>}
        {affectationsAValider > 0 && (
          <BandeauInfo ton="alerte" titre={`${affectationsAValider} affectation${affectationsAValider > 1 ? 's' : ''} à valider`}>
            Une affectation qui commence avant aujourd'hui modifie des journées passées : elle ne s'applique qu'après validation par une autre personne habilitée.
          </BandeauInfo>
        )}

        <Onglets onglets={onglets} actif={onglet} onChange={setOnglet} libelle="Horaires" />
        <div role="tabpanel" id={`panneau-${onglet}`} aria-labelledby={`onglet-${onglet}`} style={{ marginTop: 12 }}>
          {onglet === 'modeles' && <OngletModeles droits={droits} modeles={modeles.donnees} onAction={agir} ouvrir={setFenetre} />}
          {onglet === 'affectations' && <Carte><OngletAffectations droits={droits} affectations={affectations.donnees} ouvrir={setFenetre} onAction={agir} /></Carte>}
          {onglet === 'particuliers' && <Carte><OngletParticuliers droits={droits} particuliers={particuliers.donnees} ouvrir={setFenetre} /></Carte>}
          {onglet === 'verifier' && <Carte titre="Ce que lira le calcul du temps" Icone={Search}><OngletVerifier options={options} /></Carte>}
        </div>

        {fenetre?.type === 'modele' && <FenetreModele base={fenetre.base} sites={options.sites} onFermer={() => setFenetre(null)} onTermine={terminer} />}
        {fenetre?.type === 'affecter' && <FenetreAffectation modeles={modeles.donnees || []} options={options} onFermer={() => setFenetre(null)} onTermine={terminer} />}
        {fenetre?.type === 'particulier' && <FenetreHoraireJour options={options} moi={droits?.moi} onFermer={() => setFenetre(null)} onTermine={terminer} />}
        {fenetre?.type === 'archiver' && (
          <FenetreMotif titre="Archiver la version" Icone={Archive} onFermer={() => setFenetre(null)}
            onConfirmer={async (motif) => { await ecrire('patch', `/modeles/${fenetre.version.id}/archiver`, { motif }); terminer('Version archivée.'); }} />
        )}
        {fenetre?.type === 'terminer' && (
          <FenetreMotif titre="Terminer l'affectation" Icone={CalendarX} avecDate onFermer={() => setFenetre(null)}
            onConfirmer={async (motif, date) => { await ecrire('patch', `/affectations/${fenetre.affectation.id}/terminer`, { motif, date_fin: date }); terminer('Affectation terminée.'); }} />
        )}
        {fenetre?.type === 'annulerAffectation' && (
          <FenetreMotif titre="Annuler l'affectation" Icone={Ban} onFermer={() => setFenetre(null)}
            onConfirmer={async (motif) => { await ecrire('patch', `/affectations/${fenetre.affectation.id}/annuler`, { motif }); terminer('Affectation annulée.'); }} />
        )}
        {fenetre?.type === 'annulerParticulier' && (
          <FenetreMotif titre="Annuler l'horaire du jour" Icone={Ban} onFermer={() => setFenetre(null)}
            onConfirmer={async (motif) => { await ecrire('patch', `/horaires-particuliers/${fenetre.horaire.id}/annuler`, { motif }); terminer('Horaire du jour annulé.'); }} />
        )}
        {droits && !droits.lecture && <BandeauInfo ton="danger">Vous n'avez pas accès aux réglages du temps.</BandeauInfo>}
      </div>
    </Layout>
  );
}
